// src/components/StaffLevelProgressPanel.jsx
// スタッフ別 × 術式レベル別 の自立状況パネル
// - スタッフごとに、各レベルの術式について「自立達成数 / 総数」を表示
// - 年月推移 (月別自立認定数の積み上げ折れ線)
// - フィルタ: 入職年次 / スタッフ絞り込み

import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend as ReLegend,
} from 'recharts';

const StaffLevelProgressPanel = ({ demoData = null }) => {
  const { currentOrganization } = useOrganization();
  const [staffList, setStaffList] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [levels, setLevels] = useState([]);
  const [independence, setIndependence] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterYear, setFilterYear] = useState('all');
  const [filterStaffId, setFilterStaffId] = useState('all');
  const [trendStaffId, setTrendStaffId] = useState('all'); // 🔥 月別推移の対象スタッフ
  const [trendMode, setTrendMode] = useState('cumulative'); // cumulative | monthly

  useEffect(() => {
    if (demoData) {
      setStaffList(demoData.staffList || []);
      setProcedures(demoData.procedures || []);
      setLevels(demoData.levels || []);
      setIndependence(demoData.independence || []);
      return;
    }
    if (!currentOrganization?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [stfRes, prcRes, lvRes, indRes] = await Promise.all([
          supabase.from('staff').select('id, employee_id, last_name, first_name, year')
            .eq('organization_id', currentOrganization.id),
          supabase.from('procedures').select('id, name, department_id, level_id')
            .eq('organization_id', currentOrganization.id),
          supabase.from('procedure_levels').select('id, name, color, display_order')
            .eq('organization_id', currentOrganization.id)
            .order('display_order'),
          supabase.from('staff_independence_records')
            .select('id, staff_id, procedure_id, role, recognized_date')
            .eq('organization_id', currentOrganization.id),
        ]);
        setStaffList(stfRes.data || []);
        setProcedures(prcRes.data || []);
        setLevels(lvRes.data || []);
        setIndependence(indRes.data || []);
      } catch (e) {
        console.error('スタッフレベル進捗取得失敗:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentOrganization?.id, demoData]);

  // スタッフごと × レベルごとの「達成数 / 総術式数」
  const matrix = useMemo(() => {
    const procByLevel = new Map(); // level_id → procedure[]
    procedures.forEach((p) => {
      const k = p.level_id || '__unclassified__';
      if (!procByLevel.has(k)) procByLevel.set(k, []);
      procByLevel.get(k).push(p);
    });
    const indByStaffProc = new Map(); // (staff_id × procedure_id) → 自立済み (true)
    independence.forEach((r) => {
      indByStaffProc.set(`${r.staff_id}__${r.procedure_id}`, true);
    });

    const filteredStaff = staffList.filter((s) => {
      if (filterStaffId !== 'all' && s.id !== filterStaffId) return false;
      if (filterYear !== 'all' && String(s.year || '') !== String(filterYear)) return false;
      return true;
    });

    const rows = filteredStaff.map((s) => {
      const cells = {};
      levels.forEach((lv) => {
        const procs = procByLevel.get(lv.id) || [];
        const achieved = procs.filter((p) => indByStaffProc.has(`${s.id}__${p.id}`)).length;
        cells[lv.id] = { achieved, total: procs.length };
      });
      // 未分類
      const unclassified = procByLevel.get('__unclassified__') || [];
      cells.__unclassified__ = {
        achieved: unclassified.filter((p) => indByStaffProc.has(`${s.id}__${p.id}`)).length,
        total: unclassified.length,
      };
      return { staff: s, cells };
    });
    return rows;
  }, [staffList, procedures, levels, independence, filterStaffId, filterYear]);

  // 月別自立認定数の推移 (過去 12 ヶ月) - 指定スタッフに絞り込み可能
  const monthlyTrend = useMemo(() => {
    const months = [];
    for (let i = 11; i >= 0; i--) {
      months.push(dayjs().subtract(i, 'month').format('YYYY-MM'));
    }
    const target = trendStaffId === 'all'
      ? independence
      : independence.filter((r) => r.staff_id === trendStaffId);
    const map = new Map();
    months.forEach((m) => map.set(m, { month: m, count: 0, cumulative: 0 }));
    target.forEach((r) => {
      const m = (r.recognized_date || '').slice(0, 7);
      if (map.has(m)) map.get(m).count += 1;
    });
    // 累計を計算 (過去分も含めて、表示開始月時点の累計から積み上げ)
    let runningTotal = 0;
    const minMonth = months[0];
    target.forEach((r) => {
      if ((r.recognized_date || '').slice(0, 7) < minMonth) runningTotal += 1;
    });
    const result = [];
    let cum = runningTotal;
    months.forEach((m) => {
      const v = map.get(m);
      cum += v.count;
      result.push({ month: m, count: v.count, cumulative: cum });
    });
    return result;
  }, [independence, trendStaffId]);

  const uniqueYears = useMemo(() => {
    const ys = [...new Set(staffList.map((s) => s.year).filter((y) => y != null))];
    return ys.sort();
  }, [staffList]);

  return (
    <div style={{
      background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
          📈 スタッフ × レベル別 自立進捗
        </div>
        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={selStyle}>
            <option value="all">全年次</option>
            {uniqueYears.map((y) => <option key={y} value={y}>{y}年入職</option>)}
          </select>
          <select value={filterStaffId} onChange={(e) => setFilterStaffId(e.target.value)} style={selStyle}>
            <option value="all">全スタッフ</option>
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.last_name} {s.first_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div style={{ color: '#94a3b8', padding: 20, textAlign: 'center' }}>読み込み中...</div>}
      {!loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={th}>スタッフ</th>
                  <th style={th}>年次</th>
                  {levels.map((lv) => (
                    <th key={lv.id} style={{
                      ...th,
                      background: `${lv.color || '#3b82f6'}22`,
                      color: lv.color || '#3b82f6',
                    }}>{lv.name}</th>
                  ))}
                  <th style={{ ...th, color: '#94a3b8' }}>未分類</th>
                </tr>
              </thead>
              <tbody>
                {matrix.length === 0 && (
                  <tr><td colSpan={levels.length + 3} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                    該当スタッフがいません
                  </td></tr>
                )}
                {matrix.map(({ staff, cells }) => (
                  <tr key={staff.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                      {staff.last_name} {staff.first_name}
                    </td>
                    <td style={td}>{staff.year || '-'}</td>
                    {levels.map((lv) => {
                      const c = cells[lv.id] || { achieved: 0, total: 0 };
                      const pct = c.total > 0 ? Math.round((c.achieved / c.total) * 100) : 0;
                      return (
                        <td key={lv.id} style={td}>
                          {c.total === 0 ? '-' : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <div style={{ fontWeight: 700, color: pct >= 80 ? '#16a34a' : pct >= 50 ? '#ca8a04' : '#dc2626' }}>
                                {c.achieved}/{c.total}
                              </div>
                              <div style={{ width: '80%', height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                                <div style={{
                                  width: `${pct}%`, height: '100%',
                                  background: lv.color || '#3b82f6',
                                }} />
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td style={td}>
                      {cells.__unclassified__.total === 0 ? '-' :
                        `${cells.__unclassified__.achieved}/${cells.__unclassified__.total}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 月別推移 (スタッフ別フィルタ + 累計/月別切替) */}
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                📅 月別 自立認定数 (過去 12 ヶ月)
                {trendStaffId !== 'all' && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b', fontWeight: 400 }}>
                    — {staffList.find((s) => s.id === trendStaffId)?.last_name} {staffList.find((s) => s.id === trendStaffId)?.first_name}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={trendStaffId} onChange={(e) => setTrendStaffId(e.target.value)} style={selStyle}>
                  <option value="all">👥 全スタッフ合計</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.last_name} {s.first_name}
                    </option>
                  ))}
                </select>
                <select value={trendMode} onChange={(e) => setTrendMode(e.target.value)} style={selStyle}>
                  <option value="cumulative">累計</option>
                  <option value="monthly">月別</option>
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <ReLegend />
                {trendMode === 'cumulative' ? (
                  <Line type="monotone" dataKey="cumulative" name="累計自立数" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                ) : (
                  <Line type="monotone" dataKey="count" name="月別認定数" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

const selStyle = { padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 };
const th = { padding: 8, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 11, border: '1px solid #e5e7eb' };
const td = { padding: 8, textAlign: 'center', fontSize: 12 };

export default StaffLevelProgressPanel;
